'use client';

import { useState, useEffect, useRef } from 'react';
import { Link } from '@/components/LocaleLink';
import Image from 'next/image';
import { ShoppingCart, User, Menu, CarFront, Heart, Building2, Bell, ChevronDown, Check, Tag, Zap, BookOpen, ShieldCheck, Truck, RotateCcw, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCartStore } from '@/stores/cart';
import { useAuthStore } from '@/stores/auth';
import { useCustomerTypeStore } from '@/stores/customer-type';
import { useLocalePath } from '@/components/LocaleLink';
import { useRouter, usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { CategoryNav } from '@/components/CategoryNav';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';

const SearchBar = dynamic(
  () => import('@/components/SearchBar').then((m) => ({ default: m.SearchBar })),
  { loading: () => <Input type="search" placeholder="" disabled /> }
);

const MobileMenuPanel = dynamic(
  () => import('@/components/MobileMenuPanel').then((m) => ({ default: m.MobileMenuPanel })),
  { ssr: false }
);

const UserMenuDropdown = dynamic(
  () => import('@/components/UserMenuDropdown').then((m) => ({ default: m.UserMenuDropdown })),
  { ssr: false }
);
const ChangeVehicleDrawer = dynamic(
  () => import('@/components/ChangeVehicleDrawer').then((m) => ({ default: m.ChangeVehicleDrawer })),
  { ssr: false }
);
const VehicleSearchDropdown = dynamic(
  () => import('@/components/VehicleSearchDropdown').then((m) => ({ default: m.VehicleSearchDropdown })),
  { ssr: false }
);

export function Header({ freeShippingThreshold }: { freeShippingThreshold: string }) {
  const itemCount = useCartStore((state) => state.getItemCount());
  const selectedVehicle = useCartStore((state) => state.selectedVehicle);
  const customer = useAuthStore((state) => state.customer);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const logout = useAuthStore((state) => state.logout);
  const customerType = useCustomerTypeStore((state) => state.customerType);
  const hasChosenType = useCustomerTypeStore((state) => state.hasChosen);
  const setCustomerType = useCustomerTypeStore((state) => state.setCustomerType);
  const router = useRouter();
  const localePath = useLocalePath();
  const pathname = usePathname();
  const { locale } = useLocale();
  const threshold = freeShippingThreshold;
  const [mounted, setMounted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuMounted, setMobileMenuMounted] = useState(false);
  const [changeVehicleOpen, setChangeVehicleOpen] = useState(false);
  const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);


  const [unreadCount, setUnreadCount] = useState(0);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Measure header height for dropdown positioning
  useEffect(() => {
    if (!headerRef.current) return;
    const update = () => setHeaderHeight(headerRef.current?.offsetHeight || 0);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [mounted, selectedVehicle]);

  // Load notification count when authenticated
  useEffect(() => {
    if (!mounted) return;
    if (isAuthenticated()) {
      import('@/lib/api').then(({ api }) => {
        api.getNotifications()
          .then((data) => setUnreadCount(data.unread_count))
          .catch(() => {});
      });
    }
  }, [mounted, isAuthenticated]);

  const handleLogout = async () => {
    logout();
    const { toast } = await import('sonner');
    toast.success(t(locale, 'header.loggedOut'));
    router.push(localePath('/'));
  };

  const katalogHref = selectedVehicle?.ktype
    ? `/katalog?ktype=${selectedVehicle.ktype}`
    : '/katalog';

  return (
    <header ref={headerRef} className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Förtroenderad — ENDAST desktop.
          Mobilvarianten är borttagen 2026-09-03: den kostade 30 av headerns
          185 px på 360×640 och upprepade löften som står i sidfoten och på
          produktsidan. Samma information, men inte överst på varje sida. */}
      <div className="hidden lg:block bg-secondary border-b">
        <div className="container mx-auto px-4">
          <div className="hidden lg:flex items-center justify-center gap-6 py-1.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" />{t(locale, 'header.specialists')}</span>
            <span className="text-border">|</span>
            <span className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-primary" />{t(locale, 'header.freeShipping', { threshold })}</span>
            <span className="text-border">|</span>
            <span className="flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5 text-primary" />{t(locale, 'header.returnPolicy')}</span>
            <span className="text-border">|</span>
            <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-primary" />{t(locale, 'trust.securePayment')}</span>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4">
        {/* Top bar */}
        <div className="flex h-16 items-center justify-between gap-2 md:gap-4">
          {/* Mobile menu trigger */}
          <Button variant="ghost" size="icon" onClick={() => { setMobileMenuOpen(true); setMobileMenuMounted(true); }} className="lg:hidden h-10 w-10">
            <Menu className="h-5 w-5" />
            <span className="sr-only">{t(locale, 'header.menu')}</span>
          </Button>

          {/* Mobile menu panel (lazy-loaded on first open, stays mounted for close animation) */}
          {mobileMenuMounted && <MobileMenuPanel open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />}

          {/* Logo — capped on mobile so cart icon stays on-screen */}
          <Link href="/" className="flex items-center min-w-0 shrink">
            <Image
              src="/logo.png"
              alt="Autofrance"
              width={327}
              height={40}
              className="h-7 sm:h-8 lg:h-10 w-auto max-w-[120px] sm:max-w-[180px] lg:max-w-none"
              priority
            />
          </Link>

          {/* Actions — never shrink, cart must stay reachable */}
          <div className="flex items-center gap-1 shrink-0">
            <Link href={katalogHref} aria-label={t(locale, 'header.catalogue')}>
              <Button variant="ghost" size="sm" className="hidden lg:flex text-sm">
                {t(locale, 'header.catalogue')}
              </Button>
            </Link>

            <Link href={selectedVehicle?.ktype ? `/express-leverans?ktype=${selectedVehicle.ktype}` : '/express-leverans'} aria-label={t(locale, 'header.express')}>
              <Button variant="ghost" size="sm" className="hidden lg:flex text-sm gap-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50">
                <Zap className="h-3.5 w-3.5" />
                {t(locale, 'header.express')}
              </Button>
            </Link>

            <Link href="/erbjudanden" aria-label={t(locale, 'header.deals')}>
              <Button variant="ghost" size="sm" className="hidden lg:flex text-sm gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50">
                <Tag className="h-3.5 w-3.5" />
                {t(locale, 'header.deals')}
              </Button>
            </Link>

            <Link href="/bilar" aria-label={locale === 'sv' ? 'Bilar till salu' : 'Cars for sale'}>
              <Button variant="ghost" size="sm" className="hidden lg:flex text-sm gap-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                <CarFront className="h-3.5 w-3.5" />
                {locale === 'sv' ? 'Bilar till salu' : 'Cars for sale'}
              </Button>
            </Link>

            <Link href="/blogg" aria-label={t(locale, 'header.guides')}>
              <Button variant="ghost" size="sm" className="hidden lg:flex text-sm gap-1.5">
                <BookOpen className="h-3.5 w-3.5" />
                {t(locale, 'header.guides')}
              </Button>
            </Link>

            <Link href="/foretag" aria-label={t(locale, 'header.business')}>
              <Button variant="ghost" size="sm" className="hidden lg:flex text-sm gap-1.5">
                <Building2 className="h-4 w-4" />
                {t(locale, 'header.business')}
              </Button>
            </Link>

            {mounted && isAuthenticated() ? (
              <UserMenuDropdown
                initial={customer?.firstname?.charAt(0) || 'K'}
                firstname={customer?.firstname}
                lastname={customer?.lastname}
                email={customer?.email}
                onLogout={handleLogout}
              />
            ) : (
              <Link href="/login" aria-label={t(locale, 'header.login')}>
                <Button variant="ghost" size="icon" className="hidden lg:flex" aria-label={t(locale, 'header.login')} tabIndex={-1}>
                  <User className="h-5 w-5" />
                </Button>
              </Link>
            )}

            {/* Fordonsväljare — ENDAST mobil.
                Satt tidigare i mobilblockets sökfält (onVehicleClick). När det
                blocket togs bort 2026-09-03 hade fordonsvalet försvunnit helt
                från mobilen, och /sok har ingen egen väljare. Vehicle-filtrerad
                trafik står för merparten av orderraderna, så knappen flyttade
                hit i stället — samma antal tryck, noll extra pixlar. */}
            <button
              onClick={() => setVehicleDropdownOpen(true)}
              className="lg:hidden flex items-center gap-1.5 h-10 px-2.5 rounded-md border text-xs font-medium max-w-[128px] hover:bg-accent transition-colors"
              aria-label={t(locale, 'search.selectVehicle')}
            >
              <CarFront className="h-4 w-4 text-primary shrink-0" />
              <span className="truncate">
                {mounted && selectedVehicle?.manufacturer
                  ? selectedVehicle.manufacturer
                  : t(locale, 'search.selectVehicle')}
              </span>
            </button>

            {/* Wishlist */}
            <Link href="/konto#onskeLista" className="hidden lg:flex">
              <Button variant="ghost" size="icon" aria-label={t(locale, 'header.wishlist')} tabIndex={-1}>
                <Heart className="h-5 w-5" />
              </Button>
            </Link>

            {/* Notifications bell */}
            {mounted && isAuthenticated() && (
              <Link href="/konto#notiser" className="hidden lg:flex">
                <Button variant="ghost" size="icon" className="relative" aria-label={t(locale, 'header.notifications')} tabIndex={-1}>
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  )}
                </Button>
              </Link>
            )}

            {/* Customer type dropdown — only for anonymous visitors (desktop only, mobile has its own toggle below) */}
            {mounted && hasChosenType && !isAuthenticated() && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="hidden lg:flex items-center gap-1 h-8 px-2.5 rounded-md border text-xs font-medium hover:bg-accent transition-colors">
                    {customerType === 'private' ? t(locale, 'header.inclVat') : t(locale, 'header.exclVat')}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem
                    onClick={() => setCustomerType('private')}
                    className="flex items-start gap-3 py-2.5 cursor-pointer"
                  >
                    <User className="h-4 w-4 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <span className="font-medium text-sm">{t(locale, 'header.private')}</span>
                      <p className="text-xs text-muted-foreground">{t(locale, 'header.privateSub')}</p>
                    </div>
                    {customerType === 'private' && <Check className="h-4 w-4 mt-0.5 text-primary shrink-0" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setCustomerType('business')}
                    className="flex items-start gap-3 py-2.5 cursor-pointer"
                  >
                    <Building2 className="h-4 w-4 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <span className="font-medium text-sm">{t(locale, 'header.businessType')}</span>
                      <p className="text-xs text-muted-foreground">{t(locale, 'header.businessSub')}</p>
                    </div>
                    {customerType === 'business' && <Check className="h-4 w-4 mt-0.5 text-primary shrink-0" />}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Varukorgen — ENDAST desktop. Bottennaven har en egen
                varukorgsikon med samma räknare på mobil, så headerns var en
                dubblett av något som redan syns permanent längst ned. */}
            <Button
              variant="ghost"
              size="icon"
              className="relative hidden lg:flex h-10 w-10 shrink-0 bg-blue-50 hover:bg-blue-100 text-[#0055A4]"
              aria-label={t(locale, 'header.cart')}
              onClick={() => {
                if (pathname === '/varukorg') {
                  router.back();
                } else {
                  router.push('/varukorg');
                }
              }}
            >
              <ShoppingCart className="h-5 w-5" />
              {mounted && itemCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center px-1 py-0 text-[10px] font-bold"
                >
                  {itemCount > 99 ? '99+' : itemCount}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Search row (desktop) — search + vehicle picker merged into one control */}
        <div className="hidden lg:flex items-center gap-3 py-2.5 border-t">
          <div className="flex-1">
            <SearchBar onVehicleClick={() => setVehicleDropdownOpen(true)} />
          </div>
          <div className="hidden lg:flex items-center gap-3 shrink-0 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5 bg-green-50 text-green-700 px-2.5 py-1 rounded-md font-medium">
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
              {t(locale, 'header.freeShipping', { threshold })}
            </span>
            <span className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-md font-medium">
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
              {t(locale, 'header.returnPolicy')}
            </span>
            <span className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-2.5 py-1 rounded-md font-medium">
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
              {t(locale, 'header.campaignBadge')}
            </span>
          </div>
        </div>

        {/* ChangeVehicleDrawer (opened from the vehicle picker) */}
        {changeVehicleOpen && (
          <ChangeVehicleDrawer open={changeVehicleOpen} onClose={() => setChangeVehicleOpen(false)} />
        )}

        {/* Mobile search + vehicle picker — merged into one control:
            the tappable car chip inside the search box opens the vehicle selector. */}
        {/* Mobilblocket (sökfält + Kategorier + momsväxlare) är borttaget
            2026-09-03. Det kostade 90 av headerns 185 px på 360×640 och allt i
            det fanns någon annanstans: Sök och Katalog i bottennaven,
            momsväxlaren i hamburgarmenyn (MobileMenuPanel, samma villkor).
            Fordonsväljaren satt dock BARA här — den flyttade till huvudraden
            ovan som en kompakt knapp, eftersom vehicle-filtrerad trafik är den
            starkaste säljkanalen och /sok saknar fordonsväljare. */}

        {/* Vehicle search dropdown */}
        {vehicleDropdownOpen && (
          <VehicleSearchDropdown
            open={vehicleDropdownOpen}
            onClose={() => setVehicleDropdownOpen(false)}
            headerHeight={headerHeight}
          />
        )}
      </div>
      <CategoryNav />
    </header>
  );
}
